import {
  Component,
  Input,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import * as Plotly from 'plotly.js-dist-min';
import { PdfService } from '../../../shared/services/pdfMake/pdf.service';
import { ChartDataService } from '../../../shared/services/plotly/chart-data.service';
import { PlotlyService } from '../../../shared/services/plotly/plotly.service';

@Component({
  selector: 'app-charts',
  standalone: true,
  templateUrl: './charts.component.html',
  styleUrls: ['./charts.component.css']
})
export class ChartsComponent implements AfterViewInit, OnDestroy {
  @Input() statisticData!: any;
  @Input() chartDataInput!: any;
  @Input() productData!: any;
  @Input() isCustomData!: boolean;
  @Input() sectionID!: any;

  chartData: any[] = [];
  isAfterViewInit = false;
  chartId!: string;

  private resizeListener!: () => void;
  private resizeTimeout: any;

  private readonly CHART_TYPE_MAP: { [key: string]: Plotly.PlotType } = {
    'line': 'scatter',
    'bar': 'bar',
    'horizontalBar': 'bar',
    'scatter': 'scatter',
    'donut': 'pie',
    'pie': 'pie',
    '3d_scatter_plot': 'scatter3d',
    'line_bar_combined': 'scatter' // Neuer Charttyp
  };

  constructor(
    private chartDataService: ChartDataService,
    private plotlyService: PlotlyService,
    private _pdfService: PdfService
  ) {}

  ngAfterViewInit(): void {
    this.loadChartData();
    this.isAfterViewInit = true;
    this.resizeListener = this.onWindowResize.bind(this);
    window.addEventListener('resize', this.resizeListener);
  }

  private onWindowResize(): void {
    const chartElement = document.getElementById(this.chartId);
    if (chartElement) {
      Plotly.Plots.resize(chartElement);
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.handleDoubleClick();
      }, 250);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeListener);
  }

  private loadChartData(): void {
    this.chartId = this.generateChartId();
    if (this.isCustomData) {
      this.setupCustomChartData();
      this.renderCustomChart();
      return;
    }

    const chartType: Plotly.PlotType =
      this.CHART_TYPE_MAP[this.statisticData[0].chart] || 'scatter';

    if (
      chartType === 'scatter' ||
      this.statisticData[0].chart === 'line_bar_combined'
    ) {
      this.setupProcessedChartData();
      this.renderChart();
    } else {
      this.setupProductAndBenchmarkData();
      this.renderCustomChart();
    }
  }

  private generateChartId(): string {
    return 'chart-' + this.statisticData[0].id;
  }

  private setupCustomChartData(): void {
    this.chartData = [
      { data: this.chartDataInput.data, name: this.chartDataInput.title }
    ];
  }

  private setupProcessedChartData(): void {
    this.chartData = this.chartDataService.processChartData(
      this.chartDataInput,
      this.productData
    );
  }

  private setupProductAndBenchmarkData(): void {
    this.chartData.push({
      data: this.chartDataInput.product_data,
      name: this.productData.translations['en'].title
    });
    this.chartDataInput.benchmark_data.forEach((item: any) => {
      this.chartData.push({ data: item.data, name: item.name });
    });
  }

  private renderChart(): void {
    const selectedChart = this.statisticData[0]?.chart || 'scatter';
    const isGroupedBarChart = this.chartData.length > 1;
    const xRange: [string, string] | undefined = this.getInitialXRange();
    const config = { displayModeBar: false, responsive: true };

    let traces: Partial<Plotly.PlotData>[] = [];
    let layout: Partial<Plotly.Layout>;

    if (selectedChart === 'table') {
      traces = this.plotlyService.generateTracesForTableChart(this.chartData);
      layout = { margin: { t: 10, b: 10, l: 10, r: 10 } };
    } else if (selectedChart === 'line_bar_combined') {
      // Übergibt verticalLineDates an createCombinedLineBarTraces
      traces = this.plotlyService.createCombinedLineBarTraces(this.chartData, this.productData.phases);
      layout = this.plotlyService.getBaseLayoutCombined(
        isGroupedBarChart,
        this.productData.phases,
        xRange
      );
    } else if (selectedChart === 'horizontalBar') {
      traces = this.plotlyService.createHorizontalBarTraces(this.chartData);
      layout = this.plotlyService.getBaseLayout(
        isGroupedBarChart,
        this.productData.phases,
        xRange
      );
    } else {
      const chartType = selectedChart as Plotly.PlotType;
      // Zerlegt die Traces in ihre Bestandteile, inklusive Shapes/Annotations
      const result = this.plotlyService.createTraces(
        this.chartData,
        chartType,
        this.productData.phases
      );
      traces = result.traces;
      layout = this.plotlyService.getBaseLayout(
        isGroupedBarChart,
        undefined,
        xRange
      );
      // Falls noch nicht initialisiert, Shapes und Annotations initialisieren
      if (!layout.shapes) {
        layout.shapes = [];
      }
      if (!layout.annotations) {
        layout.annotations = [];
      }
      // Vertikale Shapes/Annotations hinzufügen
      layout.shapes = layout.shapes.concat(result.verticalShapes);
      layout.annotations = layout.annotations.concat(result.verticalAnnotations);
    }

    this.plotlyService
      .renderChart(this.chartId, traces, layout, config)
      .then(() => {
        this.addEventListeners();
      });
    this.addChartToPdf(this.chartId);
  }


  private renderCustomChart(): void {
    const chartMeta = this.statisticData[0];
    const chartType = this.getChartType(chartMeta.chart);
    const chartId = `chart-${chartMeta.id}`;
    const traces = this.generateTraces(chartType, this.isCustomData, chartMeta);
    const layout = this.plotlyService.generateLayout(chartType, traces, chartMeta.chart);
    const config = this.generateConfig();

    Plotly.newPlot(chartId, traces, layout, config).then(() => {
      const annotations = this.plotlyService.generateAnnotationsForNA(traces);
      Plotly.relayout(chartId, { annotations });
    });

    this.addChartToPdf(chartId);
  }

  private getChartType(statisticChart: string): Plotly.PlotType {
    return this.CHART_TYPE_MAP[statisticChart] || 'scatter';
  }

  private generateTraces(
    chartType: Plotly.PlotType,
    isCustomData: boolean,
    chartMeta: any
  ): Partial<Plotly.PlotData>[] {
    if (chartMeta.chart === 'horizontalBar') {
      console.log('horizontalBar', this.chartData);
      return this.plotlyService.createHorizontalBarTraces(this.chartData);
    }
    if (chartType === 'pie') {
      return this.chartData.map(item =>
        this.plotlyService.generateTracesForPieChart(item, isCustomData, chartMeta.chart)
      );
    }
    if (chartType === 'scatter3d' && isCustomData) {
      return this.chartData.map((item, index) =>
        this.plotlyService.generateTracesFor3DScatterChart(item, index)
      );
    }
    if (isCustomData) {
      return this.plotlyService.generateTracesFor2DCharts(
        chartType,
        this.chartDataInput.data.rows,
        true,
        this.chartDataInput,
        chartMeta.chart
      );
    }
    return this.chartData.map((item, index) =>
      this.plotlyService.generateTracesForDefault2DCharts(item, index, chartType, chartMeta.chart)
    );
  }

  private generateConfig(): Partial<Plotly.Config> {
    return { displayModeBar: false, showTips: false, responsive: true };
  }

  private getInitialXRange(): [string, string] | undefined {
    if (!this.chartData?.[0]?.data?.length) return undefined;
    const dates = this.chartData[0].data.map((point: any) => new Date(point.date));
    const minDate = new Date(Math.min(...dates.map((d: any) => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map((d: any) => d.getTime())));
    return [
      minDate.toISOString().split('T')[0],
      maxDate.toISOString().split('T')[0]
    ];
  }

  private addEventListeners(): void {
    this.plotlyService.addEventListeners(
      this.chartId,
      () => this.handleDoubleClick(),
      (xRange: any) => this.handleZoomNormalization(xRange)
    );
  }

  private handleDoubleClick(): void {
    this.chartData = this.chartDataService.processChartData(
      this.chartDataInput,
      this.productData
    );
    this.renderChart();
  }

  private handleZoomNormalization(xRange: [string, string]): void {
    const visibleData = this.chartData.map(item => {
      const filteredData = item.data.filter((point: any) => {
        const pointDate = new Date(point.date).getTime();
        return (
          pointDate >= new Date(xRange[0]).getTime() &&
          pointDate <= new Date(xRange[1]).getTime()
        );
      });
      const normalizedData = this.chartDataService.normalizeDataToStartAt100(
        filteredData.map((point: any) => ({ ...point, isBenchmark: item.isBenchmark }))
      );
      return {
        data: normalizedData,
        name: item.name,
        isBenchmark: item.isBenchmark
      };
    });
    this.chartData = visibleData;
    this.renderChart();
  }

  private async addChartToPdf(chartId: string): Promise<void> {
    const svgString = await Plotly.toImage(chartId, {
      format: 'svg',
      width: 800,
      height: 300
    });
    const cleanedSvgString = decodeURIComponent(
      svgString.replace('data:image/svg+xml,', '')
    )
      .replace(/\ufeff/g, '')
      .replace(/%0A/g, '')
      .replace(/%20/g, ' ')
      .replace(/%22/g, '"')
      .replace(/%3D/g, '=')
      .replace(/%3A/g, ':')
      .replace(/%3B/g, ';')
      .trim();
    const imageObject = { id: this.sectionID, image: cleanedSvgString };
    this._pdfService.appendChart(imageObject);
  }
}
